import Moment from 'moment';
import { logger } from 'utils/debug';

Moment.defaultFormat = 'DD/MM/YYYY';

export const dateRegExps = [/^\d{4}-\d{2}-\d{2}$/, /^\d{2}-\d{2}-\d{4}$/];
export const DATE_FORMATS = ['YYYY-MM-DD', 'DD-MM-YYYY'];

export const fullDateRegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d+)?(-\d{2}:\d{2})?(Z)?$/;
export const dateAmericaMazatlanRegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{6}-\d{2}:\d{2}/;
export const dateTimeArizonaTimeRegexExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{6}/;
export const dateTimeJuarezRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSSS';
export const DATETIME_FORMAT_ALT = 'YYYY-MM-DDTHH:mm:ss';
export const DATE_NETPAY_FORMAT = 'YYYY/MM/DD';
export const TIME_FORMAT = 'hh:mm:ss';
export const DATE_FRONTEND_FORMAT = 'DD/MM/YYYY';
export const DATETIME_FRONTEND_FORMAT = 'DD/MM/YYYY  HH:mm A';
export const TIMEPICKER_FORMAT = 'h:mm a';

const isJSON = (val) => {
  try {
    const parsed = JSON.parse(val);
    return parsed;
  } catch {
    return false;
  }
};

const formatReceivedValues = (values = {}, log) => {
  const keys = Object.keys(values);
  const clone = {};
  keys.forEach((prop) => {
    const value = values[prop];
    clone[prop] = value;
    // // check to discart falsy values
    // if ((value === null || value === undefined || value === '')) {
    //   delete clone[prop];
    // }
    const regexIdx = dateRegExps.findIndex((regx) => regx.test(value));
    if (regexIdx !== -1) {
      clone[prop] = new Moment(value, [DATE_FORMATS[regexIdx]]);
    }
    if (fullDateRegExp.test(value)) {
      clone[prop] = new Moment(value, [DATETIME_FORMAT_ALT, DATETIME_FORMAT]);
    }
    if (dateAmericaMazatlanRegExp.test(value)) {
      clone[prop] = new Moment(value, [DATE_FORMAT]);
    }
    if (dateTimeArizonaTimeRegexExp.test(value)) {
      clone[prop] = new Moment(value, [DATETIME_FORMAT]);
    }
    if (dateTimeJuarezRegex.test(value)) {
      clone[prop] = new Moment(value, [DATETIME_FORMAT]);
    }

    if ((prop === 'direccion' || prop === 'domicilio')) {
      const parsedValue = isJSON(value);
      if (/^[0-9]$/.test(value)) {
        clone[prop] = value;
      } else if (parsedValue) {
        clone[prop] = parsedValue;
      }
    }
  });
  clone.key = clone.id;
  if (log) logger('[clone]', clone);
  return clone;
};

export const formatReceived = (values, log) => {
  if (Array.isArray(values)) {
    return values.map((item) => formatReceivedValues(item, log));
  }
  if (Array.isArray(values?.results)) {
    return { ...values, results: values.results.map((item) => formatReceivedValues(item, log)) };
  }
  return formatReceivedValues(values, log);
};

const defaultFormatter = (values, options = {}, fullDate = false) => {
  const {
    files = [],
    formData: asFormData,
    clean = true,
    deletableKeys = [],
  } = options;

  const clone = {};

  Object.entries(values).forEach(([prop, value]) => {
    if (value === null || value === undefined || value === '') {
      if (clean && !deletableKeys.includes(prop)) return;
      clone[prop] = asFormData ? '' : null;
      return;
    }

    if (Array.isArray(value) && value.length > 0) {
      clone[prop] = value.map((item) => {
        if (Moment.isMoment(item)) {
          return item.format(
            fullDate === true || (Array.isArray(fullDate) && fullDate.includes(prop))
              ? `${DATE_FORMAT}[T]HH:mm:ss[Z]` : DATE_FORMAT,
          );
        }
        if (typeof item === 'object') return defaultFormatter(item, options, fullDate);
        return item;
      });
      return;
    }

    if (value instanceof Date) {
      clone[prop] = new Moment(value).format(DATE_FORMAT);
      return;
    }

    if (Array.isArray(value) && Moment.isMoment(value[0])) {
      clone[prop] = value.map((v) => v.format(
        fullDate === true || (Array.isArray(fullDate) && fullDate.includes(prop))
          ? `${DATE_FORMAT}[T]HH:mm:ss[Z]` : DATE_FORMAT,
      ));
      return;
    }
    if (Moment.isMoment(value)) {
      clone[prop] = value.format(
        fullDate === true || (Array.isArray(fullDate) && fullDate.includes(prop))
          ? `${DATE_FORMAT}[T]HH:mm:ss[Z]` : DATE_FORMAT,
      );
      return;
    }

    if (files.includes(prop)) {
      if (value.file instanceof File) {
        clone[prop] = value.file;
      } else {
        clone[prop] = asFormData || clean ? undefined : null;
      }
      return;
    }

    if (['direccion', 'domicilio', 'domicilio_fiscal'].includes(prop)) {
      const parsedValue = isJSON(value);
      clone[prop] = /^[0-9]$/.test(value) ? value : parsedValue || value;
      return;
    }

    clone[prop] = value;
  });

  if (asFormData) {
    return Object.entries(clone).reduce((formData, [key, val]) => {
      formData.append(key, val);
      return formData;
    }, new FormData());
  }

  return clone;
};

export default defaultFormatter;

export const discartNullValues = (values = {}, config = {}) => {
  const {
    numbers = false,
  } = config;
  const keys = Object.keys(values);
  const clone = {};
  keys.forEach((prop) => {
    const value = values[prop];
    if (value !== null && value !== 'N/A' && value !== '' && value !== undefined) {
      clone[prop] = value;
    }
    if (numbers) {
      if (/^(\d+)?[.]?(\d+)?$/.test(value)) {
        clone[prop] = parseFloat(value) >= 0 ? parseFloat(value) : null;
      } else {
        clone[prop] = null;
      }
    }
  });
  return clone;
};

export const hasFiles = (values = {}, files = []) => files
  .some((prop) => values[prop] && typeof values[prop] !== 'string');

export const removeAccents = (text = '') => {
  if (!text) return text;
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const dateFormatterToReporteador = (date, inicio = true, separator = '/') => {
  if (!date) return '';
  const dateFormat = DATE_FORMAT.replace(/-/g, separator);
  const INIT_HOURS = '00:00:00.000';
  const END_HOURS = '23:59:59.999';
  if (Moment.isMoment(date)) {
    if (inicio) {
      return `${date.format(dateFormat)} ${INIT_HOURS}`;
    }
    return `${date.format(dateFormat)} ${END_HOURS}`;
  }
  const momentInstance = new Moment(date);
  if (inicio) {
    return `${momentInstance.format(dateFormat)} ${INIT_HOURS}`;
  }
  return `${momentInstance.format(dateFormat)} ${END_HOURS}`;
};
